/**
 * InteractiveAudioReader — EAA-1755.
 *
 * Reusable "listen and read along" building block: a play/pause control with the
 * passage words highlighting in sync (via the content-kit `SyncedPassage`
 * primitive). Two audio sources:
 *   1. AI-generated model audio (`audioUrl` + `wordTimestamps`) — preferred.
 *   2. Browser Text-to-Speech (Web Speech API) fallback when no audio has been
 *      generated yet, with word highlighting driven by utterance `onboundary`.
 *
 * Content types compose this rather than re-implementing audio + karaoke wiring.
 */

import * as React from 'react';
import {
    SyncedPassage,
    CircleIconButton,
    activeWordIndexAt,
    wordStartOffsets,
    wordIndexForChar,
    estimateWordDurations,
    cn,
    type ReadingWordTimestamp,
    type SyncedWordScore,
} from '@ai-content-hub/content-kit';
import { Play, Pause } from 'lucide-react';

export interface InteractiveAudioReaderProps {
    /** The passage to display and read. */
    text: string;
    /** Model audio URL; when absent, browser TTS is used as a fallback. */
    audioUrl?: string;
    /** Per-word timing that drives the highlight (AI-audio mode). */
    wordTimestamps?: ReadingWordTimestamp[];
    /** Per-word pronunciation buckets for colour feedback after grading. */
    wordScores?: SyncedWordScore[];
    /** Tooltip heading for a word's score in grading review (e.g. "Score"). */
    scoreLabel?: string;
    /** Optional label above the passage. */
    title?: string;
    disabled?: boolean;
    /** BCP-47 language for the browser-TTS fallback voice. Defaults to `en-US`. */
    ttsLang?: string;
    /** Localised labels (host supplies translated strings). */
    labels?: { play?: string; pause?: string; noAudio?: string };
    /** Extra controls rendered next to the play button (e.g. a record button). */
    controlsSlot?: React.ReactNode;
    className?: string;
}

const ttsSupported = typeof window !== 'undefined' && 'speechSynthesis' in window;

export const InteractiveAudioReader: React.FC<InteractiveAudioReaderProps> = ({
    text,
    audioUrl,
    wordTimestamps = [],
    wordScores,
    scoreLabel,
    disabled = false,
    ttsLang = 'en-US',
    labels,
    controlsSlot,
    className,
}) => {
    const audioRef = React.useRef<HTMLAudioElement | null>(null);
    const utteranceRef = React.useRef<SpeechSynthesisUtterance | null>(null);
    // Browser-TTS word timings captured live from onboundary, and the play clock
    // (rAF) that highlights by elapsed play time.
    const timingsRef = React.useRef<{ index: number; time: number }[]>([]);
    const estimatedRef = React.useRef<{ index: number; time: number }[]>([]);
    const startRef = React.useRef(0);
    const rafRef = React.useRef<number | null>(null);
    const [isPlaying, setIsPlaying] = React.useState(false);
    const [activeWord, setActiveWord] = React.useState(-1);
    // TTS has no native pause/seek; remember the word to resume from (-1 = idle).
    const [pausedWord, setPausedWord] = React.useState(-1);

    const hasAudio = Boolean(audioUrl);
    // Browser-TTS fallback is available when there is no AI audio but the passage
    // has text and the platform supports speechSynthesis.
    const canUseTts = !hasAudio && ttsSupported && text.trim().length > 0;
    const canPlay = hasAudio || canUseTts;

    const wordOffsets = React.useMemo(
        () => (canUseTts ? wordStartOffsets(text) : []),
        [canUseTts, text]
    );

    // ── AI-audio mode ──────────────────────────────────────────────────────
    const handleTimeUpdate = React.useCallback(() => {
        const el = audioRef.current;
        if (!el || !wordTimestamps.length) return;
        // Add a small delay to the time update to ensure correct word highlighting
        setActiveWord(activeWordIndexAt(wordTimestamps, el.currentTime + 0.01   ));
    }, [wordTimestamps]);

    const handleAudioToggle = React.useCallback(() => {
        const el = audioRef.current;
        if (!el) return;
        if (el.paused) {
            void el.play();
        } else {
            el.pause();
        }
    }, []);

    const handleAudioEnded = React.useCallback(() => {
        setIsPlaying(false);
        setActiveWord(-1);
    }, []);

    // ── Browser-TTS fallback mode ──────────────────────────────────────────
    const stopTtsClock = React.useCallback(() => {
        if (rafRef.current != null) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
    }, []);

    // Speak the passage from a given word index. TTS has no native seek/resume,
    // so "resume" and "click a word" both re-speak from that word (substring),
    // mapping local boundaries back to absolute word indices.
    const speakFrom = React.useCallback(
        (startWord: number) => {
            const synth = window.speechSynthesis;
            const words = text.match(/\S+/g) ?? [];
            if (words.length === 0) return;
            const clamp = Math.max(0, Math.min(startWord, words.length - 1));
            const startChar = wordOffsets[clamp] ?? 0;
            const subText = text.slice(startChar);

            const speakNow = () => {
                const utterance = new SpeechSynthesisUtterance(subText);
                utterance.lang = ttsLang;
                const langPrefix = ttsLang.slice(0, 2).toLowerCase();
                const candidates = synth
                    .getVoices()
                    .filter((v) => v.lang?.toLowerCase().startsWith(langPrefix));
                // Prefer a LOCAL voice: Chrome only fires onboundary for on-device
                // voices, not network voices, which play audio but never highlight.
                const match = candidates.find((v) => v.localService) ?? candidates[0];
                if (match) utterance.voice = match;

                // Estimated schedule (absolute indices from `clamp`), used when no
                // onboundary events arrive (network voices, Safari/Firefox). Word
                // durations include a punctuation pause (after , . " etc.) so the
                // highlight dwells where the synthesizer naturally pauses.
                const durations = estimateWordDurations(text, 0.075 / (utterance.rate || 1));
                const schedule: { index: number; time: number }[] = [];
                let acc = 0;
                for (let i = clamp; i < words.length; i += 1) {
                    schedule.push({ index: i, time: acc });
                    acc += durations[i] ?? 0;
                }
                estimatedRef.current = schedule;

                // Live onboundary timings on the play clock; charIndex is relative
                // to the sub-text, so add startChar before mapping to a word.
                timingsRef.current = [];
                let seq = clamp;
                utterance.onboundary = (event) => {
                    if (event.name && event.name !== 'word') return;
                    const abs = wordIndexForChar(wordOffsets, event.charIndex + startChar);
                    const index = abs >= clamp ? abs : seq;
                    seq = index + 1;
                    timingsRef.current.push({
                        index,
                        time: (performance.now() - startRef.current) / 1000,
                    });
                };
                utterance.onend = () => {
                    stopTtsClock();
                    setIsPlaying(false);
                    setPausedWord(-1);
                    setActiveWord(-1);
                };
                utterance.onerror = () => {
                    stopTtsClock();
                    setIsPlaying(false);
                };
                utteranceRef.current = utterance;
                setActiveWord(clamp);
                setIsPlaying(true);
                setPausedWord(-1);

                startRef.current = performance.now();
                stopTtsClock();
                const tick = () => {
                    const elapsed = (performance.now() - startRef.current) / 1000;
                    const list = timingsRef.current.length
                        ? timingsRef.current
                        : estimatedRef.current;
                    let idx = -1;
                    for (let i = 0; i < list.length; i += 1) {
                        if (list[i].time <= elapsed) idx = list[i].index;
                        else break;
                    }
                    if (idx >= 0) setActiveWord(idx);
                    rafRef.current = requestAnimationFrame(tick);
                };
                rafRef.current = requestAnimationFrame(tick);

                synth.speak(utterance);
            };

            if (synth.speaking || synth.pending || synth.paused) synth.cancel();
            if (synth.getVoices().length > 0) {
                speakNow();
                return;
            }
            let spoken = false;
            const speakOnce = () => {
                if (spoken) return;
                spoken = true;
                synth.removeEventListener('voiceschanged', speakOnce);
                speakNow();
            };
            synth.addEventListener('voiceschanged', speakOnce);
            setTimeout(speakOnce, 300);
        },
        [text, ttsLang, wordOffsets, stopTtsClock]
    );

    // Pause TTS: cancel speech but remember the current word so a later play
    // resumes from there; keep the highlight on that word.
    const pauseTts = React.useCallback(() => {
        window.speechSynthesis.cancel();
        stopTtsClock();
        setIsPlaying(false);
        setPausedWord(activeWord >= 0 ? activeWord : 0);
    }, [activeWord, stopTtsClock]);

    const handleTtsToggle = React.useCallback(() => {
        const synth = window.speechSynthesis;
        if (isPlaying || synth.speaking || synth.pending) {
            pauseTts();
        } else {
            speakFrom(pausedWord >= 0 ? pausedWord : 0);
        }
    }, [isPlaying, pausedWord, pauseTts, speakFrom]);

    // Click a word → continue from there (seek for AI audio; re-speak for TTS).
    const seekToWord = React.useCallback(
        (index: number) => {
            if (hasAudio) {
                const el = audioRef.current;
                if (!el) return;
                const ts = wordTimestamps[index]?.start;
                // Add a small delay to the seek to ensure correct word highlighting
                el.currentTime = typeof ts === 'number' ? ts + 0.01: el.currentTime + 0.01;
                setActiveWord(index);
                void el.play();
            } else if (canUseTts) {
                speakFrom(index);
            }
        },
        [hasAudio, canUseTts, wordTimestamps, speakFrom]
    );

    // Stop any in-flight speech on unmount or when switching to AI audio.
    React.useEffect(() => {
        return () => {
            stopTtsClock();
            if (ttsSupported) window.speechSynthesis.cancel();
        };
    }, [stopTtsClock]);
    React.useEffect(() => {
        if (hasAudio && ttsSupported) {
            stopTtsClock();
            window.speechSynthesis.cancel();
        }
    }, [hasAudio, stopTtsClock]);

    const handlePlayPause = hasAudio ? handleAudioToggle : handleTtsToggle;

    return (
        <div
            className={cn(
                'interactive-audio-reader rounded-lg border border-border bg-card p-5 shadow-sm',
                className
            )}
        >
            <SyncedPassage
                text={text}
                activeWordIndex={activeWord}
                // AI-audio mode: align tokens to the agent's word segmentation so
                // hyphenated compounds ("co-located") highlight and seek correctly.
                // TTS mode uses naive whitespace indices, so pass none.
                wordTimestamps={hasAudio ? wordTimestamps : undefined}
                wordScores={wordScores}
                scoreLabel={scoreLabel}
                onWordClick={canPlay ? seekToWord : undefined}
                className="interactive-audio-reader-passage"
            />

            <div className="interactive-audio-reader-controls mt-5 flex items-center justify-center gap-3">
                {canPlay && (
                    <>
                        <CircleIconButton
                            variant="primary"
                            onClick={handlePlayPause}
                            disabled={disabled}
                            aria-label={isPlaying ? labels?.pause : labels?.play}
                            className="interactive-audio-reader-play"
                        >
                            {isPlaying ? (
                                <Pause className="h-5 w-5" />
                            ) : (
                                <Play className="ml-0.5 h-5 w-5" />
                            )}
                        </CircleIconButton>
                        {hasAudio && (
                            <audio
                                ref={audioRef}
                                src={audioUrl}
                                preload="metadata"
                                onPlay={() => setIsPlaying(true)}
                                onPause={() => setIsPlaying(false)}
                                onEnded={handleAudioEnded}
                                onTimeUpdate={handleTimeUpdate}
                                className="hidden"
                            />
                        )}
                    </>
                )}
                {controlsSlot}
                {!canPlay && !controlsSlot && (
                    <p className="interactive-audio-reader-noaudio text-sm text-muted-foreground">
                        {labels?.noAudio}
                    </p>
                )}
            </div>
        </div>
    );
};

export default InteractiveAudioReader;
